import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import {
  AuditLogService,
  AuditLogEntry,
  AuditLogResponse
} from 'src/app/_share/_services/audit-log.service';
import { NotificationService } from 'src/app/_share/_services/notification.service';
import { FormFields } from 'src/app/_share/_models/forms/form-fields.interface';
import { TableMetaData } from 'src/app/_share/_models/components/table-meta-data.model';
import { DatePipe } from '@angular/common';
import { TableColumns } from 'src/app/_share/_models/components/table-columns.model';

@Component({
  selector: 'app-audit-log-dialog',
  templateUrl: './audit-log-dialog.component.html',
  styleUrls: ['./audit-log-dialog.component.scss'],
  providers: [DatePipe]
})
export class AuditLogDialogComponent implements OnInit, OnDestroy {
  auditLogs: AuditLogEntry[] = [];
  auditLogs$: BehaviorSubject<AuditLogEntry[]> = new BehaviorSubject<AuditLogEntry[]>([]);
  loading: boolean = false;
  totalRecords: number = 0;
  currentPage: number = 0;
  pageSize: number = 50;
  dialogData: any;
  currentOffset: number = 0;

  // Filter form
  filterForm: FormGroup;

  // Available filter fields
  filterFields = [
    { label: 'Action Type', value: 'action_type' },
    { label: 'Entity Type', value: 'entity_type' },
    { label: 'User ID', value: 'user_id' },
    { label: 'IP Address', value: 'ip_address' },
    { label: 'Source', value: 'source' },
    { label: 'Context', value: 'context' }
  ];

  // table columns
  columns: TableColumns[] = [
    {
      field: 'action_type',
      header: 'Action',
      formatter: (log) => ({
        tag: {
          value: this.getActionDisplayName(log),
          severity: this.getSeverityColor(log.action_type)
        }
      })
    },

    { field: 'entity_type', header: 'Entity Type' },
    {
      field: 'user_id',
      header: 'User ID',
      formatter: (row) => ({ primary: `User: ${row.user_id?.toString()}` })
    },
    { field: 'ip_address', header: 'IP Address' },
    { field: 'source', header: 'Source' },
    { field: 'context', header: 'Context' },
    {
      field: 'action_date',
      header: 'Timestamp',
      formatter: (row) => ({
        icon: 'pi pi-calendar',
        primary: this.getTimeAgo(row.action_date),
        secondary: new Date(row.action_date).toLocaleString()
      })
    }
  ];

  // Available operators
  operators = [
    { label: 'Equals', value: 'eq' },
    { label: 'Contains', value: 'contains' },
    { label: 'Starts With', value: 'starts_with' },
    { label: 'Ends With', value: 'ends_with' },
    { label: 'Greater Than', value: 'gt' },
    { label: 'Less Than', value: 'lt' }
  ];

  // Date range
  dateRange: Date[] = [];

  // Advanced filters
  advancedFilters: any[] = [];

  private destroy$ = new Subject<void>();

  constructor(
    private ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private fb: FormBuilder,
    private auditLogService: AuditLogService,
    private messageService: MessageService,
    private notificationService: NotificationService,
    private datePipe: DatePipe
  ) {
    this.filterForm = this.fb.group({
      actionType: [''],
      entityType: [''],
      userId: [''],
      ipAddress: [''],
      source: [''],
      context: [''],
      dateFrom: [''],
      dateTo: ['']
    });
  }

  metadata: TableMetaData = {
    totalRecords: 0,
    rows: 100,
    first: 0,
    loading: true,
    globalFilter: '',
    sort: '',
    id: ''
  };

  ngOnInit(): void {
    this.dialogData = this.config.data || {};
    this.loadAuditLogs();
    console.log('Dialog data:', this.dialogData);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadAuditLogs(): void {
    this.metadata.loading = true;
    const filters = this.buildFilters();
    console.log('context data in loadAuditLogs: ', this.dialogData.context_data);

    this.auditLogService
      .getAuditLogs(
        {
          limit: this.metadata.rows,
          offset: this.metadata.first,
          q: this.metadata.globalFilter,
          sort: this.metadata.sort,
          id: this.metadata.id
        },
        filters,
        this.dialogData.context_data
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: AuditLogResponse) => {
          this.auditLogs = response.data;
          this.auditLogs$.next(this.auditLogs);
          this.metadata.totalRecords = response.count;
          this.metadata.loading = false;
          this.totalRecords = response.count;
        },
        error: (error) => {
          console.error('Error loading audit logs:', error);
          this.loading = false;
          this.metadata.loading = false;
          this.notificationService.showError(
            'Load Failed',
            'Failed to load audit logs',
            'system-user'
          );
        }
      });
  }

  buildFilters(): any {
    const filters: any = {};
    const formValue = this.filterForm.value;

    // Basic filters
    if (formValue.actionType) filters.action_type = formValue.actionType;
    if (formValue.entityType) filters.entity_type = formValue.entityType;
    if (formValue.userId) filters.user_id = formValue.userId;
    if (formValue.ipAddress) filters.ip_address = formValue.ipAddress;
    if (formValue.source) filters.source = formValue.source;
    if (formValue.context) filters.context = formValue.context;

    // Date filters
    if (formValue.dateFrom) filters.date_from = formValue.dateFrom;
    if (formValue.dateTo) filters.date_to = formValue.dateTo;

    // Advanced filters
    if (this.advancedFilters.length > 0) {
      filters.advanced = this.advancedFilters;
    }

    return filters;
  }

  applyFilters(): void {
    this.currentPage = 0;
    this.loadAuditLogs();
  }

  clearFilters(): void {
    this.filterForm.reset();
    this.advancedFilters = [];
    this.dateRange = [];
    this.currentPage = 0;
    this.loadAuditLogs();
  }

  addAdvancedFilter(): void {
    this.advancedFilters.push({
      field: '',
      operator: 'eq',
      value: ''
    });
  }

  removeAdvancedFilter(index: number): void {
    this.advancedFilters.splice(index, 1);
  }

  exportToCSV(): void {
    // Implementation for CSV export
    this.notificationService.showInfo(
      'Export',
      'CSV export functionality will be implemented',
      'system-user'
    );
  }

  closeDialog(): void {
    this.ref.close();
  }

  getSeverityColor(severity: string): string {
    const actionType = severity.toLowerCase();
    if (actionType.includes('create') || actionType.includes('success')) return 'success';
    if (actionType.includes('update') || actionType.includes('modify')) return 'info';
    if (actionType.includes('delete') || actionType.includes('remove')) return 'danger';
    if (actionType.includes('login') || actionType.includes('auth')) return 'warning';
    if (actionType.includes('error') || actionType.includes('fail')) return 'danger';
    return 'info';
  }

  getTimeAgo(timestamp: string): string {
    const now = new Date();
    const logTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now.getTime() - logTime.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} min ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
  }

  getActionDisplayName(log: AuditLogEntry): string {
    return log.action_description || log.action_type || 'Unknown Action';
  }

  getActionCategory(log: AuditLogEntry): string {
    return log.entity_type || log.action_type || 'System';
  }

  viewLogDetails(log: AuditLogEntry): void {
    // Show detailed information in a side panel or expandable row
    console.log('Log details:', log);
  }
}
